import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BlockItem, WidgetConfig } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import { CanonicalWidgetDataSchema } from '@trinity/chat/types/dashboard-data';
import type { DashboardAgentOption } from '@trinity/chat-components/containers/Dashboard/context';
import { DashboardDataContext, useDashboardConfig } from '@trinity/chat-components/containers/Dashboard/context';
import type { DataProvider } from './DataProvider';

/** 5-minute polling interval for agent-sourced widgets in live mode */
const LIVE_POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Collect all WidgetConfig instances from the nested block → item → widget structure */
function collectAllWidgets(config: ReturnType<typeof useDashboardConfig>['config']): WidgetConfig[] {
  const widgets: WidgetConfig[] = [];
  for (const block of config.blocks) {
    collectWidgetsFromBlock(block.items, widgets);
  }
  return widgets;
}

function collectWidgetsFromBlock(items: BlockItem[], widgets: WidgetConfig[]): void {
  for (const item of items) {
    if (item.type === 'widget') {
      widgets.push(item.widget);
    } else if (item.type === 'section') {
      for (const bw of item.section.widgets) {
        widgets.push(bw.widget);
      }
    }
  }
}

type Props = {
  children: React.ReactNode;
  /** The DataProvider to use for fetching widget data */
  provider: DataProvider;
  /**
   * `builder` — fetch once on mount; no polling (data previewed in the editor).
   * `view`    — fetch once on mount from snapshots; no polling (management app viewer).
   * `live`    — fetch on mount then poll every 5 minutes (agent widgets refresh).
   * Default: `live`.
   */
  mode?: 'builder' | 'view' | 'live';
  /**
   * Builder-only: used by the "Fetch Preview" button to proxy a request through
   * an agent and inject the result as a live preview.
   */
  proxyFetch?: (data: { agentId: number; extensionPath: string; brandIdentifier?: string }) => Promise<CanonicalWidgetData | null>;
  /** Optional brand identifier — forwarded to proxyFetch so agent requests include brand context. */
  brandIdentifier?: string;
  /**
   * Builder-only: list of available HTTP agents that the user can select from
   * when configuring an agent data source on a widget.
   */
  agents?: DashboardAgentOption[];
};

const EMPTY_AGENTS: DashboardAgentOption[] = [];

/**
 * DashboardDataContainer — connects a DataProvider to DashboardDataContext.
 *
 * Each host app wraps the dashboard with its appropriate DataProvider:
 * - Management app (builder): `ConfigAwareDataProvider`, mode="builder"
 * - Chat app (renderer): `ConfigAwareDataProvider`, mode="live"
 * - Sentiment frontend: `ConfigAwareDataProvider`, mode="live"
 */
export function DashboardDataContainer({ children, provider, mode = 'live', proxyFetch, brandIdentifier, agents = EMPTY_AGENTS }: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const [widgetData, setWidgetData] = useState<Map<string, CanonicalWidgetData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  // Keep a stable ref to the provider so the useEffect can always access the latest one
  const providerRef = useRef(provider);
  providerRef.current = provider;

  // Extract all widgets from the nested block structure
  const allWidgets = useMemo(() => collectAllWidgets(config), [config]);
  // Stable ref so polling callbacks don't re-fire on every config change
  const allWidgetsRef = useRef(allWidgets);
  allWidgetsRef.current = allWidgets;

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await providerRef.current.fetchAllWidgetData(allWidgetsRef.current);
      setWidgetData(data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshWidget = useCallback(async (widgetId: string) => {
    const widget = allWidgetsRef.current.find(w => w.id === widgetId);
    if (!widget) return;
    const data = await providerRef.current.fetchWidgetData(widgetId, widget);
    if (data !== null) {
      setWidgetData(prev => {
        const next = new Map(prev);
        next.set(widgetId, data);
        return next;
      });
    }
  }, []);

  // Fetch all data on mount and whenever widget configurations change.
  // allWidgets recomputes (new reference) whenever config changes, so this effect
  // re-fires on every Apply in the builder — giving the user an immediate preview.
  // Debounced after first render to avoid re-fetching agent-source widgets on
  // every keystroke in the builder.
  const isFirstFetch = useRef(true);
  useEffect(() => {
    if (isFirstFetch.current) {
      isFirstFetch.current = false;
      void fetchAll();
      return;
    }
    const timeout = setTimeout(() => { void fetchAll(); }, 300);
    return () => clearTimeout(timeout);
  }, [fetchAll, allWidgets]);

  // Live mode only: poll agent-sourced widgets every 5 minutes
  useEffect(() => {
    if (mode !== 'live') return;
    const id = setInterval(() => { void fetchAll(); }, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mode, fetchAll]);

  // proxyFetch ref so the callback is always up-to-date without re-creating fetchAgentPreview
  const proxyFetchRef = useRef(proxyFetch);
  proxyFetchRef.current = proxyFetch;

  const updateWidgetData = useCallback((widgetId: string, data: CanonicalWidgetData | null) => {
    setWidgetData(prev => {
      const next = new Map(prev);
      if (data === null) {
        next.delete(widgetId);
      } else {
        next.set(widgetId, data);
      }
      return next;
    });
  }, []);

  const brandIdentifierRef = useRef(brandIdentifier);
  brandIdentifierRef.current = brandIdentifier;

  const fetchAgentPreview = useCallback(async (agentId: number, extensionPath: string, widgetId: string): Promise<CanonicalWidgetData | null> => {
    if (!proxyFetchRef.current) return null;
    try {
      const raw = await proxyFetchRef.current({ agentId, extensionPath, brandIdentifier: brandIdentifierRef.current });
      if (raw !== null && raw !== undefined) {
        const parsed = CanonicalWidgetDataSchema.safeParse(raw);
        if (!parsed.success) return null;
        // Safe cast: safeParse validated the shape; hand-written CanonicalWidgetData
        // mirrors the schema with stricter required fields.
        const data = parsed.data as CanonicalWidgetData;
        updateWidgetData(widgetId, data);
        return data;
      }
      return null;
    } catch {
      return null;
    }
  }, [updateWidgetData]);

  const value = useMemo(() => ({
    widgetData,
    isLoading,
    refreshWidget,
    refreshAll: fetchAll,
    fetchAgentPreview,
    updateWidgetData,
    agents,
  }), [widgetData, isLoading, refreshWidget, fetchAll, fetchAgentPreview, updateWidgetData, agents]);

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}
